import type { DateTime } from "luxon"

import { isMultiDay } from "../../../../../../lib/time"
import { LocalCache, StorageKey } from "../../../../../../shared/cache/LocalCache"
import { MeetingAudienceFilterType } from "../../../../../../shared/components/filters/orderTypeFilter/helpers"
import { OrganizerType } from "../../../../../../shared/types/OrganizerTypes"
import { MeetingAudience, ServiceCenterBookingOrganizer } from "../../serviceCenterHomeTypes"
import type { ServiceCenterBooking, ServiceCenterMeetingRow } from "../../serviceCenterHomeTypes"
import type { Resource as BookedResource } from "../../../../../nonAdminUser/reports/upcomingOrders/resourceBookings/userResourceBookingTypes"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import type { AppLocalization } from "../../../../../localization/appLocalization"
import type { DateTimeFactory } from "../../../../../localization/useDateTime"
import { GuestInfoState, GuestInfoType } from "../../../guestList/guestListTypes"
import type { GuestInfo } from "../../../guestList/guestListTypes"

function toResourceType(value: string): ResourceType | undefined {
    return Object.values(ResourceType).find((type) => type === value)
}

export function toResourceTypes(resources: ReadonlyArray<BookedResource>): ResourceType[] {
    const types = resources
        .map((resource) => toResourceType(resource.resourceType))
        .filter((type): type is ResourceType => type !== undefined)
    return Array.from(new Set(types))
}

export const BOOKING_TABS: ReadonlyArray<ResourceType> = [ResourceType.ROOM, ResourceType.CAR, ResourceType.MOBILE_AV]

export type BookingTabCounts = Readonly<Record<ResourceType, number>>

export const EMPTY_BOOKING_TAB_COUNTS: BookingTabCounts = {
    [ResourceType.ROOM]: 0,
    [ResourceType.CAR]: 0,
    [ResourceType.MOBILE_AV]: 0,
}

export type MeetingSectionFilters = Readonly<{
    search: string
    resourceType: ResourceType
    audience: MeetingAudienceFilterType
}>

export type MeetingSectionView = Readonly<{
    meetings: ServiceCenterMeetingRow[]
    rows: ServiceCenterMeetingRow[]
    tabCounts: BookingTabCounts
    hasMeetings: boolean
}>

export function toMeetingSectionView(
    bookings: ReadonlyArray<ServiceCenterBooking>,
    { search, resourceType, audience }: MeetingSectionFilters,
): MeetingSectionView {
    const query = search.trim().toLowerCase()
    const meetings = bookings.map((booking) => ({
        ...booking,
        resourceTypes: toResourceTypes(booking.bookingDetails?.resources ?? []),
    }))
    const matching = query
        ? meetings.filter((meeting) => meeting.meetingDetails.meetingName.toLowerCase().includes(query))
        : meetings

    const matchesAudience = (meeting: ServiceCenterMeetingRow) =>
        audience === MeetingAudienceFilterType.ALL || toMeetingAudience(meeting.meetingDetails.isExternal) === audience

    const rowsFor = (type: ResourceType) =>
        matching.filter(
            (meeting) =>
                meeting.resourceTypes.includes(type) && (type !== ResourceType.ROOM || matchesAudience(meeting)),
        )

    return {
        meetings,
        rows: rowsFor(resourceType),
        hasMeetings: meetings.some((meeting) => meeting.resourceTypes.includes(ResourceType.ROOM)),
        tabCounts: {
            [ResourceType.ROOM]: rowsFor(ResourceType.ROOM).length,
            [ResourceType.CAR]: rowsFor(ResourceType.CAR).length,
            [ResourceType.MOBILE_AV]: rowsFor(ResourceType.MOBILE_AV).length,
        },
    }
}

export function loadMeetingAudienceFilter(): MeetingAudienceFilterType {
    const stored = LocalCache().get(StorageKey.SERVICE_CENTER_MEETING_AUDIENCE_FILTER)
    return Object.values(MeetingAudienceFilterType).find((filter) => filter === stored) ?? MeetingAudienceFilterType.ALL
}

export function saveMeetingAudienceFilter(filter: MeetingAudienceFilterType) {
    LocalCache().set(StorageKey.SERVICE_CENTER_MEETING_AUDIENCE_FILTER, filter)
}

export function toMeetingAudience(isExternal?: boolean): MeetingAudience | undefined {
    if (isExternal === undefined) return undefined
    return isExternal ? MeetingAudienceFilterType.EXTERNAL : MeetingAudienceFilterType.INTERNAL
}

export function getHostOrganizerName(organizers?: Array<ServiceCenterBookingOrganizer>) {
    if (!Array.isArray(organizers) || organizers.length === 0) return ""

    const responsible = organizers.find((org) => org?.type === OrganizerType.RESPONSIBLE && org?.name)
    if (responsible) return responsible.name

    const organizer = organizers.find((org) => org?.type === OrganizerType.ORGANIZER && org?.name)
    return organizer ? organizer.name : ""
}

export function getHostOrganizer(
    organizers?: Array<ServiceCenterBookingOrganizer>,
): ServiceCenterBookingOrganizer | null {
    if (!Array.isArray(organizers) || organizers.length === 0) return null

    const responsible = organizers.find((org) => org?.type === OrganizerType.RESPONSIBLE && org?.name)
    const organizer = organizers.find((org) => org?.type === OrganizerType.ORGANIZER && org?.name)
    return responsible ?? organizer ?? null
}

export function getHostOrganizerCompanyName(organizers?: Array<ServiceCenterBookingOrganizer>) {
    if (!Array.isArray(organizers) || organizers.length === 0) return ""

    const responsible = organizers.find((org) => org?.type === OrganizerType.RESPONSIBLE && org?.company?.name)
    if (responsible) return responsible.company?.name

    const organizer = organizers.find((org) => org?.type === OrganizerType.ORGANIZER && org?.company?.name)
    return organizer ? organizer.company?.name : ""
}

export type BookingDisplayRow = Readonly<{
    key: string
    start: string
    end: string
    title: string
    type: string
    host: string
    company: string
    room: string
}>

export function formatMeetingRowTimes(
    start: DateTime | undefined,
    end: DateTime | undefined,
    dateTimeFactory: DateTimeFactory,
): Readonly<{ start: string; end: string }> {
    const multiDay = isMultiDay(start, end)
    const format = (dateTime: DateTime | undefined) => {
        if (!dateTime) return ""
        const time = dateTimeFactory.formatTime(dateTime)
        return multiDay ? `${dateTimeFactory.formatMonthDay(dateTime)} ${time}` : time
    }

    return { start: format(start), end: format(end) }
}

export function toMeetingRowTimes(
    meetingDetails: Readonly<{ startDateTime: string; endDateTime: string }>,
    dateTimeFactory: DateTimeFactory,
): Readonly<{ start: string; end: string }> {
    return formatMeetingRowTimes(
        dateTimeFactory.fromApiOrUndefined(meetingDetails.startDateTime),
        dateTimeFactory.fromApiOrUndefined(meetingDetails.endDateTime),
        dateTimeFactory,
    )
}

export function toBookingDisplayRow(
    row: ServiceCenterMeetingRow,
    l10n: AppLocalization,
    dateTimeFactory: DateTimeFactory,
): BookingDisplayRow {
    const { start, end } = toMeetingRowTimes(row.meetingDetails, dateTimeFactory)
    const audience = toMeetingAudience(row.meetingDetails.isExternal)

    return {
        key: String(row.id),
        start,
        end,
        title: row.meetingDetails.meetingName,
        type: audience
            ? l10n.getStringForEnum("service-center-reports-dashboard-meetings-list-row-audience", audience, audience)
            : "",
        host: getHostOrganizerName(row.organizers),
        company: getHostOrganizerCompanyName(row.organizers) ?? "",
        room: row.meetingDetails.location ?? "",
    }
}

export type MeetingArrivals = Readonly<{
    arrived: number
    total: number
}>

const ARRIVED_GUEST_STATES: ReadonlyArray<GuestInfoState> = [GuestInfoState.CHECKED_IN, GuestInfoState.CHECKED_OUT]

export function toMeetingArrivals(guests: ReadonlyArray<GuestInfo>): ReadonlyMap<number, MeetingArrivals> {
    const arrivals = new Map<number, MeetingArrivals>()

    guests.forEach((guest) => {
        const orderId = guest.order?.id
        if (orderId === undefined) return

        const people = guest.type === GuestInfoType.GROUP ? guest.attendee.count : 1
        const current = arrivals.get(orderId) ?? { arrived: 0, total: 0 }
        arrivals.set(orderId, {
            arrived: current.arrived + (ARRIVED_GUEST_STATES.includes(guest.state) ? people : 0),
            total: current.total + people,
        })
    })

    return arrivals
}
